import { Injectable } from '@angular/core';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import {
    FetchGradedUnitPartialGrades,
    FetchGradedUnitPartialGradesFailure,
    FetchGradedUnitPartialGradesSuccess,
    SearchGradedUnitPartialGrades,
} from 'app/library/graded-unit-partial-grades/actions';
import { GradedUnitPartialGradesState } from 'app/library/graded-unit-partial-grades/models/graded-unit-partial-grades.state';
import { GradedUnit } from 'app/library/graded-unit/models/graded-unit.model';
import { FetchModulePartialGrades, FetchModulePartialGradesFailure, FetchModulePartialGradesSuccess } from 'app/library/module-partial-grades/actions';
import { ModulePartialGradesState } from 'app/library/module-partial-grades/models/module-partial-grades.state';
import { FetchParticipantPartialGrades, FetchParticipantPartialGradesFailure, FetchParticipantPartialGradesSuccess } from 'app/library/participant-partial-grades/actions';
import { ParticipantPartialGradesState } from 'app/library/participant-partial-grades/models/participant-partial-grades.state';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    BulkUpdatePartialGrades,
    BulkUpdatePartialGradesFailure,
    BulkUpdatePartialGradesStatus,
    BulkUpdatePartialGradesStatusFailure,
    BulkUpdatePartialGradesStatusSuccess,
    BulkUpdatePartialGradesSuccess,
    FetchPartialGrade,
    UpdatePartialGrade,
    UpdatePartialGradeFailure,
    UpdatePartialGradeSuccess,
} from '../actions';
import { FetchPartialGradeFailure, FetchPartialGradeSuccess } from '../actions/index';
import { PartialGradeCommand } from '../models/partial-grade.command.model';
import { PartialGrade } from '../models/partial-grade.model';
import { PartialGradeState } from '../models/partial-grade.state';

@Injectable()
export class PartialGradeService {
    @Select(ParticipantPartialGradesState.getEntitiesWithGradedUnit)
    currentUserPartialGrade$: Observable<PartialGrade[]>;

    @Select(ModulePartialGradesState.getEntities)
    modulePartialGrades$: Observable<PartialGrade[]>;

    @Select(GradedUnitPartialGradesState.getEntities)
    gradedUnitPartialGrade$: Observable<PartialGrade[]>;

    @Select(PartialGradeState.getEntity)
    currentPartialGrade$: Observable<PartialGrade>;

    get currentUserPartialGradeSnapshot() {
        return this._store.selectSnapshot(ParticipantPartialGradesState.getEntitiesWithGradedUnit);
    }

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _actionHelper: NgxsActionHelper,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {}

    getPartialGradesOfCurrentUser(withGradedUnit: boolean = false): PartialGrade[] {
        if (withGradedUnit) {
            return this._store.selectSnapshot(ParticipantPartialGradesState.getEntitiesWithGradedUnit);
        }

        return this._store.selectSnapshot(ParticipantPartialGradesState.getEntities);
    }

    getPartialGradesOfCurrentModule(withUser: boolean = false): PartialGrade[] {
        if (withUser) {
            return this._store.selectSnapshot(ModulePartialGradesState.getEntitiesWithUser);
        }

        return this._store.selectSnapshot(ModulePartialGradesState.getEntities);
    }

    getModulePartialGradesStateProperty(property: string): string {
        return this._store.selectSnapshot(ModulePartialGradesState)[property];
    }

    getPartialGradesOfCurrentGradedUnit(withUser: boolean = false): PartialGrade[] {
        if (withUser) {
            return this._store.selectSnapshot(GradedUnitPartialGradesState.getEntitiesWithUser);
        }

        return this._store.selectSnapshot(GradedUnitPartialGradesState.getEntities);
    }

    getFilteredPartialGradesOfCurrentGradedUnit(withUser: boolean = false): PartialGrade[] {
        if (withUser) {
            return this._store.selectSnapshot(GradedUnitPartialGradesState.getFilteredEntitiesWithUser);
        }

        return this._store.selectSnapshot(GradedUnitPartialGradesState.getFilteredEntities);
    }

    getGradedUnitPartialGradesStateProperty(property: string): string {
        return this._store.selectSnapshot(GradedUnitPartialGradesState)[property];
    }

    getCurrentPartialGrade(): PartialGrade {
        return this._store.selectSnapshot(PartialGradeState.getEntity);
    }

    async fetchPartialGradesOfCurrentUserByModuleId(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchParticipantPartialGrades(moduleId));

        return await this._actionHelper.race(FetchParticipantPartialGradesSuccess, FetchParticipantPartialGradesFailure).toPromise();
    }

    async fetchPartialGradesByModuleId(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchModulePartialGrades(moduleId));

        return await this._actionHelper.race(FetchModulePartialGradesSuccess, FetchModulePartialGradesFailure).toPromise();
    }

    async fetchPartialGradesByGradedUnitId(
        gradedUnit: GradedUnit,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize = this._pageSizePreferenceService.getPageSize('module-form-gradebook-tab-grades', [10, 25, 50], 10),
        pageNumber?: number
    ): Promise<boolean> {
        this._store.dispatch(new FetchGradedUnitPartialGrades({ gradedUnit, search, sortBy, sortOrder, pageSize, pageNumber }));

        return await this._actionHelper.race(FetchGradedUnitPartialGradesSuccess, FetchGradedUnitPartialGradesFailure).toPromise();
    }

    async fetchPartialGradeById(gradedUnitId: string, force: boolean = false): Promise<boolean> {
        if (!force) {
            const gradedUnit = this.getCurrentPartialGrade();

            if (gradedUnit && gradedUnit.id === gradedUnitId) {
                return true;
            }
        }

        this._store.dispatch(new FetchPartialGrade(gradedUnitId));

        return await this._actionHelper.race(FetchPartialGradeSuccess, FetchPartialGradeFailure).toPromise();
    }

    async updatePartialGrade(partialGrade: PartialGrade): Promise<boolean> {
        this._store.dispatch(new UpdatePartialGrade(partialGrade));

        return await this._actionHelper.race(UpdatePartialGradeSuccess, UpdatePartialGradeFailure).toPromise();
    }

    async bulkUpdatePartialGrades(partialGrades: PartialGradeCommand[]): Promise<boolean> {
        this._store.dispatch(new BulkUpdatePartialGrades(partialGrades));

        return await this._actionHelper.race(BulkUpdatePartialGradesSuccess, BulkUpdatePartialGradesFailure).toPromise();
    }

    async bulkUpdatePartialGradesStatus(partialGradeIds: string[], status: string): Promise<boolean> {
        this._store.dispatch(
            new BulkUpdatePartialGradesStatus({
                entities: partialGradeIds,
                status,
            })
        );

        return await this._actionHelper.race(BulkUpdatePartialGradesStatusSuccess, BulkUpdatePartialGradesStatusFailure).toPromise();
    }

    async filterPartialGradesOfCurrentGradedUnit(searchTerm: string): Promise<boolean> {
        setTimeout(() => {
            this._store.dispatch(new SearchGradedUnitPartialGrades(searchTerm));
        });

        return await this._action$
            .pipe(
                ofActionCompleted(SearchGradedUnitPartialGrades),
                map(({ result }) => result.successful),
                take(1)
            )
            .toPromise();
    }
}
